/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isUndefined, omitBy } from 'lodash';
import type { FieldValues } from 'react-hook-form';
import { layerUtils } from '@opengeoweb/store';
import {
  emptyGeoJSON,
  isGeoJSONGeometryEmpty,
  removeTransientFeatureProperties,
  rewindGeometry,
} from '@opengeoweb/webmap-react';
import type { DrawingToolFormValues } from './DrawingToolFormContents';
import type { DrawingTransformation } from '../../store/warningsDrawings/types';

export const MAX_OBJECT_NAME_LENGTH = 64;

export interface DrawingObject {
  featureId: string;
  featureIndex: number;
  objectName: string;
  storedObjectName?: string;
  selectionType?: string;
}

export const formatDefaultObjectName = (position: number): string =>
  `Object ${position}`;

const DEFAULT_OBJECT_NAME_PATTERN = /^Object (\d+)$/i;

// Returns the position or undefined
export const getDefaultObjectNamePosition = (
  name: string,
): number | undefined => {
  const match = DEFAULT_OBJECT_NAME_PATTERN.exec(name.trim());
  return match ? Number(match[1]) : undefined;
};

/**
 * Creates a generator of default object names. The generator remembers the name
 * it handed out for a feature, so a feature keeps its generated name, and
 * continues numbering after the highest default name which is already taken.
 * @returns generator of default object names, to pass to `getDrawingObjects`
 */
export const createDefaultObjectNameGenerator = (): ((
  featureId: string,
  takenNames: string[],
) => string) => {
  const generatedNamesByFeatureId = new Map<string, string>();
  let nextObjectNumber: number | undefined;

  return (featureId: string, takenNames: string[]): string => {
    const existingName = generatedNamesByFeatureId.get(featureId);
    if (existingName) {
      return existingName;
    }

    if (nextObjectNumber === undefined) {
      const takenNumbers = takenNames
        .map(getDefaultObjectNamePosition)
        .filter((position): position is number => position !== undefined);
      nextObjectNumber = takenNumbers.length
        ? Math.max(...takenNumbers) + 1
        : 1;
    }

    const name = formatDefaultObjectName(nextObjectNumber);
    nextObjectNumber += 1;
    generatedNamesByFeatureId.set(featureId, name);
    return name;
  };
};

interface EligibleFeature {
  featureId: string;
  featureIndex: number;
  storedObjectName?: string;
  selectionType?: string;
}

/**
 * Maps the features of a drawing to the objects shown in the form. Features
 * without a geometry are skipped: these are placeholders for a shape which
 * still needs to be drawn. Features without a (unique) name get a generated
 * default name.
 *
 * Stored names are claimed first (round 1) so a generated default name can
 * never steal a stored name, wherever that stored name appears in the
 * feature array. Default names for the remaining, unclaimed features are
 * then generated (round 2) by `getDefaultObjectName`.
 * @param geoJSON geoJSON of the drawing
 * @param getDefaultObjectName generates the default name for a feature
 * @returns list of objects for the drawing
 */
export const getDrawingObjects = (
  geoJSON: GeoJSON.FeatureCollection | undefined,
  getDefaultObjectName: (featureId: string, takenNames: string[]) => string,
): DrawingObject[] => {
  if (!geoJSON?.features.length) {
    return [];
  }

  const eligibleFeatures: EligibleFeature[] = [];
  geoJSON.features.forEach((feature, featureIndex) => {
    const featureId = layerUtils.getFeatureSelectionKey(feature);

    if (!featureId || isGeoJSONGeometryEmpty(feature)) {
      return;
    }

    eligibleFeatures.push({
      featureId,
      featureIndex,
      storedObjectName: feature.properties?.objectName as string | undefined,
      selectionType: feature.properties?.selectionType as string | undefined,
    });
  });

  // Round 1: claim the stored names.
  const takenNames: string[] = [];
  const claimedNamesLowerCase = new Set<string>();
  const claimedObjectNames = eligibleFeatures.map(({ storedObjectName }) => {
    const trimmedName = storedObjectName?.trim();

    if (!trimmedName || claimedNamesLowerCase.has(trimmedName.toLowerCase())) {
      return undefined;
    }

    claimedNamesLowerCase.add(trimmedName.toLowerCase());
    takenNames.push(storedObjectName!);

    return storedObjectName;
  });

  // Round 2: generate default names for the features without a claimed name.
  return eligibleFeatures.map((eligibleFeature, index) => {
    const claimedObjectName = claimedObjectNames[index];
    const objectName =
      claimedObjectName ??
      getDefaultObjectName(eligibleFeature.featureId, takenNames);

    return {
      featureId: eligibleFeature.featureId,
      featureIndex: eligibleFeature.featureIndex,
      objectName,
      storedObjectName: eligibleFeature.storedObjectName,
      selectionType: eligibleFeature.selectionType,
    };
  });
};

/**
 * Checks if the passed name is already used by another object
 * @param objectName name to check
 * @param objectNames names of all objects
 * @param ownIndex index of the object the name belongs to
 * @returns true when another object uses the same name
 */
export const isDuplicateObjectName = (
  objectName: string,
  objectNames: string[],
  ownIndex: number,
): boolean =>
  objectNames.some(
    (name, index) =>
      index !== ownIndex &&
      name?.trim().toLowerCase() === objectName?.trim().toLowerCase(),
  );

/**
 * Adds the object names of the form to the properties of the geoJSON features
 * @param geoJSON geoJSON of the drawing
 * @param objectNamesByFeatureId object name for every feature id
 * @returns geoJSON including the object names
 */
export const addObjectNamesToGeoJSON = (
  geoJSON: GeoJSON.FeatureCollection,
  objectNamesByFeatureId: Record<string, string>,
): GeoJSON.FeatureCollection => ({
  ...geoJSON,
  features: geoJSON.features.map((feature) => {
    const featureId = layerUtils.getFeatureSelectionKey(feature);
    const objectName = featureId
      ? objectNamesByFeatureId[featureId]
      : undefined;

    if (objectName === undefined) {
      return feature;
    }

    return {
      ...feature,
      properties: { ...feature.properties, objectName },
    };
  }),
});

export interface ComparableDrawing {
  objectName: string;
  geoJSON: GeoJSON.FeatureCollection;
  /**
   * the geometries of the applied mask, undefined when no mask is applied. Only
   * the geometries are compared, the same mask can be stored inside the drawing
   * and fetched as a separate drawing, which differ in their other properties.
   */
  maskGeometries?: GeoJSON.Geometry[];
}

/**
 * Generate a reference to the data that gets stored so you can compare
 * two drawings to see whether there are unsaved changes. Transient properties
 * are left out, as well as ids, undrawn shapes and winding order.
 * A masked drawing stores the clipped result in `geoJSON` and the drawing the
 * user actually made in `transformation.original`, so the original is compared,
 * together with the geometries of the mask
 * @param drawing drawing to reduce to its stored data
 * @returns the drawing, containing only the data which gets stored
 */
export const getComparableDrawing = (drawing: {
  objectName?: string;
  geoJSON?: GeoJSON.FeatureCollection;
  transformation?: DrawingTransformation | null;
}): ComparableDrawing => {
  const geoJSON =
    drawing.transformation?.original ?? drawing.geoJSON ?? emptyGeoJSON;
  const indexedGeoJSON: GeoJSON.FeatureCollection = {
    ...geoJSON,
    features: geoJSON.features.map((feature, index) => ({
      ...feature,
      id: index,
    })),
  };
  const drawingObjects = getDrawingObjects(
    indexedGeoJSON,
    createDefaultObjectNameGenerator(),
  );
  const objectNamesByFeatureId = Object.fromEntries(
    drawingObjects.map(({ featureId, objectName }) => [featureId, objectName]),
  );
  const namedGeoJSON = addObjectNamesToGeoJSON(
    indexedGeoJSON,
    objectNamesByFeatureId,
  );

  return {
    objectName: drawing.objectName?.trim() ?? '',
    geoJSON: {
      ...geoJSON,
      features: rewindGeometry(removeTransientFeatureProperties(namedGeoJSON))
        .features.filter((feature) => !isGeoJSONGeometryEmpty(feature))
        .map(({ id, properties, ...feature }) => ({
          ...feature,
          properties: omitBy(properties, isUndefined),
        })),
    },
    ...(drawing.transformation && {
      maskGeometries: drawing.transformation.mask.features.map(
        (feature) => feature.geometry,
      ),
    }),
  };
};

export const getDrawingToSubmit = (
  formValues: FieldValues,
): Omit<DrawingToolFormValues, 'id' | 'features'> => {
  // form only values which should not be submitted
  const {
    opacity,
    geoJSON,
    features = [],
    ...submitValues
  } = formValues as DrawingToolFormValues & { opacity: number };

  const objectNamesByFeatureId = features.reduce<Record<string, string>>(
    (objectNames, { featureId, objectName }) => ({
      ...objectNames,
      [featureId]: objectName,
    }),
    {},
  );

  return {
    ...submitValues,
    // rewind geometry so the winding order is correct
    geoJSON: rewindGeometry(
      removeTransientFeatureProperties(
        addObjectNamesToGeoJSON(
          geoJSON ?? emptyGeoJSON,
          objectNamesByFeatureId,
        ),
      ),
    ),
  } as Omit<DrawingToolFormValues, 'id' | 'features'>;
};
